/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRulePrioritizeNominated } from 'src/components/TradeRule';
import TradeRulePrioritizeNominatedPlaceholder from 'src/components/TradeRule/TradeRulePrioritizeNominated/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import buildTradeRulePrioritizeNominatedRetryVariables from './helpers/buildTradeRulePrioritizeNominatedRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule.trade_rule_prioritize_nominated_route.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the trade rule priority order. Please try again.',
  },
});

export const tradeRulePrioritizeNominatedRouteQuery = graphql`
  query TradeRulePrioritizeNominatedRoute_Query($uuid: ID!, $meterId: ID!) {
    property(uuid: $uuid) {
      ...TradeRulePrioritizeNominated_property
    }
    meter(uuid: $meterId) {
      ...TradeRulePrioritizeNominated_meter
    }
  }
`;

const tradeRulePrioritizeNominatedRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
      meterId: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeRulePrioritizeNominated` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule prioritize nominated route content.
 */
function TradeRulePrioritizeNominatedPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRulePrioritizeNominatedRouteQuery, queryRef);
  return <TradeRulePrioritizeNominated {...routeProps} {...queryData} />;
}

TradeRulePrioritizeNominatedPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRulePrioritizeNominatedRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRulePrioritizeNominated` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with
 * `key={`${routeProps.match.params.uuid}-${routeProps.match.params.meterId}`}`
 * so a uuid/meterId change disposes the stale `useQueryLoader` query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule prioritize nominated route content.
 */
function TradeRulePrioritizeNominatedRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRulePrioritizeNominatedRouteQuery,
    routeProps,
    {
      logLabel: 'TradeRulePrioritizeNominatedRoute',
      getRetryVariablesFunc: buildTradeRulePrioritizeNominatedRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRulePrioritizeNominatedPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRulePrioritizeNominatedPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRulePrioritizeNominated {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRulePrioritizeNominatedRouteRenderer.propTypes = {
  routeProps: tradeRulePrioritizeNominatedRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRulePrioritizeNominated route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRulePrioritizeNominated route content or loading state.
 */
export function tradeRulePrioritizeNominatedRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRulePrioritizeNominatedRouteRenderer
        key={`${routeProps.match.params.uuid}-${routeProps.match.params.meterId}`}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRulePrioritizeNominatedRouteRenderer
      key={`${props.match.params.uuid}-${props.match.params.meterId}`}
      routeProps={props}
    />
  );
}

export default TradeRulePrioritizeNominatedRouteRenderer;
